import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { authAPI } from './utils/api';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SetupPage from './pages/SetupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import PoliciesPage from './pages/PoliciesPage';
import SubscribePage from './pages/SubscribePage';
import ClaimsPage from './pages/ClaimsPage';
import TokensPage from './pages/TokensPage';
import TransactionsPage from './pages/TransactionsPage';
import PaymentsPage from './pages/PaymentsPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminClaimsPage from './pages/AdminClaimsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminAgentsPage from './pages/AdminAgentsPage';
import AgentDashboard from './pages/AgentDashboard';
import MemberDashboardPage from './pages/MemberDashboardPage';
import './index.css';


function SplashScreen({ onDone }) {
  const [fading, setFading] = React.useState(false);
  React.useEffect(() => {
    const t1 = setTimeout(() => setFading(true), 2400);
    const t2 = setTimeout(onDone, 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      background:'linear-gradient(135deg, #050E1F 0%, #0A1628 50%, #0D2137 100%)',
      display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', gap:24,
      opacity: fading ? 0 : 1,
      transition: 'opacity 0.4s ease',
    }}>
      {/* Logo */}
      <div style={{
        width:90, height:90, borderRadius:24,
        background:'linear-gradient(135deg,#00C896,#00D68F)',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:44, boxShadow:'0 0 40px rgba(0,200,150,0.4)',
        animation:'splashPop 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
      }}>🏍️</div>

      {/* Brand */}
      <div style={{textAlign:'center', animation:'splashFade 0.6s 0.3s ease both', opacity:0}}>
        <div style={{
          fontSize:36, fontWeight:900, letterSpacing:2,
          background:'linear-gradient(135deg,#00C896,#00D68F)',
          WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
        }}>PikiShield</div>
        <div style={{color:'#94A3B8', fontSize:13, letterSpacing:4, marginTop:4, textTransform:'uppercase'}}>
          Micro-Protection
        </div>
      </div>

      {/* Tagline */}
      <div style={{
        color:'#64748B', fontSize:13, marginTop:8,
        animation:'splashFade 0.6s 0.6s ease both', opacity:0,
      }}>
        Protecting every ride, every day
      </div>

      {/* Loading bar */}
      <div style={{
        position:'absolute', bottom:0, left:0, right:0, height:3,
        background:'rgba(255,255,255,0.05)',
      }}>
        <div style={{
          height:'100%', background:'linear-gradient(90deg,#00C896,#00D68F)',
          animation:'splashBar 2.8s ease forwards',
        }}/>
      </div>

      <style>{`
        @keyframes splashPop {
          from { transform: scale(0.5); opacity:0; }
          to   { transform: scale(1);   opacity:1; }
        }
        @keyframes splashFade {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes splashBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </div>
  );
}

function roleHome(role) {
  if (role === 'admin')  return '/admin';
  if (role === 'agent')  return '/agent';
  if (role === 'nok')    return '/claims';
  if (role === 'member') return '/member-dashboard';
  return '/dashboard';
}

const MOBILE_NAV = {
  rider: [
    { icon:'📊', label:'Home',      path:'/dashboard' },
    { icon:'🛡️', label:'Policies',  path:'/policies' },
    { icon:'📋', label:'Claims',    path:'/claims' },
    { icon:'💳', label:'Pay',       path:'/payments' },
    { icon:'🪙', label:'Tokens',    path:'/tokens' },
  ],
  nok: [
    { icon:'📋', label:'Claims',    path:'/claims' },
    { icon:'🔐', label:'Password',  path:'/password' },
  ],
  admin: [
    { icon:'🏢', label:'Overview',  path:'/admin' },
    { icon:'📋', label:'Claims',    path:'/admin/claims' },
    { icon:'👥', label:'Members',   path:'/admin/users' },
    { icon:'🤝', label:'Agents',    path:'/admin/agents' },
  ],
  agent: [
    { icon:'📊', label:'Dashboard', path:'/agent' },
    { icon:'🔐', label:'Password',  path:'/password' },
  ],
  member: [
    { icon:'👤', label:'Account',   path:'/member-dashboard' },
    { icon:'🔐', label:'Password',  path:'/password' },
  ],
};

function MobileTopBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  return (
    <div className="mobile-topbar">
      <div className="mobile-topbar-logo">🏍️ PikiShield</div>
      <div style={{fontSize:11,color:'rgba(255,255,255,.5)',fontWeight:600}}>
        {user.fullName?.split(' ')[0]} · {user.role}
      </div>
      <button className="mobile-topbar-notif" onClick={()=>navigate('/password')} title="Settings">⚙️</button>
      <button className="mobile-topbar-notif" onClick={logout} title="Sign out">🚪</button>
    </div>
  );
}

function MobileBottomNav() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { pathname } = useLocation();
  if (!user) return null;
  const items = MOBILE_NAV[user.role] || MOBILE_NAV.rider;
  return (
    <nav className="mobile-nav">
      <div className="mobile-nav-items">
        {items.map(item => (
          <button key={item.path}
            className={`mobile-nav-item${pathname === item.path ? ' active' : ''}`}
            onClick={() => navigate(item.path)}>
            <span className="mn-icon">{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading" style={{minHeight:'100vh'}}>Loading PikiShield…</div>;
  if (!user)   return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  return children;
}

function ForcePasswordModal() {
  const { user, refreshUser } = useAuth();
  const [form, setForm]       = useState({ current:'', next:'', confirm:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [done, setDone]       = useState(false);

  if (!user?.mustChangePassword) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return setError('Passwords do not match');
    if (form.next.length < 8)       return setError('Minimum 8 characters');
    setLoading(true); setError('');
    try {
      await authAPI.changePassword(form.current, form.next);
      await refreshUser();
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed — check your temporary password');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:99999,
      background:'rgba(10,22,40,0.92)',
      backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:20,
    }}>
      <div style={{
        background:'white', borderRadius:18,
        width:'100%', maxWidth:440,
        boxShadow:'0 24px 80px rgba(0,0,0,.4)',
        overflow:'hidden',
      }}>
        <div style={{
          background:'linear-gradient(135deg,#0A1628 0%,#06352A 100%)',
          padding:'28px 28px 24px', textAlign:'center',
        }}>
          <div style={{fontSize:42,marginBottom:10}}>🔐</div>
          <div style={{fontWeight:800,fontSize:18,color:'white',marginBottom:6}}>Set Your Permanent Password</div>
          <div style={{fontSize:13,color:'rgba(255,255,255,.6)',lineHeight:1.5}}>
            Welcome, <strong style={{color:'#00E0A8'}}>{user?.fullName?.split(' ')[0]}</strong>! Your agent registered you with a
            temporary password. Please set a permanent one to continue.
          </div>
        </div>
        <div style={{padding:'24px 28px'}}>
          {done ? (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:48,marginBottom:12}}>✅</div>
              <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>Password set successfully!</div>
              <div style={{fontSize:13,color:'var(--muted)',marginBottom:20}}>You can now access your account fully.</div>
              <button className="btn btn-primary" style={{width:'100%',justifyContent:'center'}}
                onClick={()=>window.location.reload()}>
                Continue to my account →
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#991B1B',marginBottom:16}}>❌ {error}</div>}
              <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#92400E',marginBottom:16,lineHeight:1.5}}>
                ⚠️ Enter the <strong>temporary password</strong> your agent gave you (e.g. <code>Piki1234!</code>)
              </div>
              <div className="form-group">
                <label className="form-label">Temporary Password (from your agent)</label>
                <input className="form-input" type="password" placeholder="e.g. Piki1234!"
                  value={form.current} onChange={e=>setForm(f=>({...f,current:e.target.value}))} required/>
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-input" type="password" placeholder="At least 8 characters"
                  value={form.next} onChange={e=>setForm(f=>({...f,next:e.target.value}))} required minLength={8}/>
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input className="form-input" type="password" placeholder="Repeat your new password"
                  value={form.confirm} onChange={e=>setForm(f=>({...f,confirm:e.target.value}))} required/>
                {form.confirm && form.next !== form.confirm && <p className="form-error">Passwords do not match</p>}
              </div>
              <button className="btn btn-primary btn-lg" type="submit"
                style={{width:'100%',justifyContent:'center'}}
                disabled={loading||(!!form.confirm&&form.next!==form.confirm)}>
                {loading ? '⏳ Saving…' : '🔐 Set Permanent Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function KycBanner() {
  const { user } = useAuth();
  if (!user || user.kycStatus !== 'pending' || !['rider','member'].includes(user.role)) return null;
  return (
    <div style={{
      background: 'linear-gradient(90deg, #FEF3C7, #FFFBEB)',
      borderBottom: '2px solid #F59E0B',
      padding: '10px 24px',
      display: 'flex', alignItems: 'center', gap: 10,
      fontSize: 13,
    }}>
      <span style={{fontSize:18}}>⏳</span>
      <div>
        <strong>Account pending verification</strong> — Your KYC is being reviewed by an admin.
        You can view your profile but cannot subscribe, submit claims or earn tokens until approved.
      </div>
    </div>
  );
}

function AppLayout({ children }) {
  return (
    <div className="app-shell">
      <Sidebar />
      <MobileTopBar />
      <main className="main-content">
        <KycBanner />
        {children}
      </main>
      <MobileBottomNav />
      <ForcePasswordModal />
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const home = user ? roleHome(user.role) : '/login';
  return (
    <Routes>
      <Route path="/setup"           element={<SetupPage />} />
      <Route path="/login"           element={user ? <Navigate to={home} replace/> : <LoginPage />} />
      <Route path="/register"        element={user ? <Navigate to={home} replace/> : <RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      <Route path="/dashboard"    element={<ProtectedRoute roles={['rider']}><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/policies"     element={<ProtectedRoute roles={['rider']}><AppLayout><PoliciesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/subscribe"    element={<ProtectedRoute roles={['rider']}><AppLayout><SubscribePage /></AppLayout></ProtectedRoute>} />
      <Route path="/tokens"       element={<ProtectedRoute roles={['rider']}><AppLayout><TokensPage /></AppLayout></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute roles={['rider']}><AppLayout><TransactionsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payments"     element={<ProtectedRoute roles={['rider']}><AppLayout><PaymentsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/claims"       element={<ProtectedRoute roles={['rider','nok']}><AppLayout><ClaimsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/password"     element={<ProtectedRoute roles={['rider','nok','agent','admin','member']}><AppLayout><ChangePasswordPage /></AppLayout></ProtectedRoute>} />
      <Route path="/member-dashboard" element={<ProtectedRoute roles={['member']}><AppLayout><MemberDashboardPage /></AppLayout></ProtectedRoute>} />

      <Route path="/admin"         element={<ProtectedRoute roles={['admin']}><AppLayout><AdminDashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/claims"  element={<ProtectedRoute roles={['admin']}><AppLayout><AdminClaimsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/users"   element={<ProtectedRoute roles={['admin']}><AppLayout><AdminUsersPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/agents"  element={<ProtectedRoute roles={['admin']}><AppLayout><AdminAgentsPage /></AppLayout></ProtectedRoute>} />

      <Route path="/agent" element={<ProtectedRoute roles={['agent']}><AppLayout><AgentDashboard /></AppLayout></ProtectedRoute>} />

      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

export default function App() {
  const [splash, setSplash] = React.useState(true);
  const handleSplashDone = React.useCallback(() => setSplash(false), []);

  return (
    <>
      {splash && <SplashScreen onDone={handleSplashDone} />}
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </>
  );
}
